/** @jsxImportSource @emotion/react */
import {
  useRef,
  useState,
  useMemo,
  ComponentType,
  useCallback,
  useEffect,
} from 'react';
import { useMerchantApplicationContext, useSnackbar } from 'contexts';
import { useSearchParams } from 'react-router-dom';
import {
  StepComponentProps,
  mapToMerchantApplicationSignUpDataModel,
} from 'types/interfaces/sign-up/MerchantApplication.types';
import {
  isAbortError,
  isFieldError,
  isFormError,
  MappedError,
} from 'types/common/ApiHelpers';
import { SignUpStepKey, signUpStepsConfig } from './signUpConfig';
import { logError, logEvent, useSignUpFunctions } from 'utils';
import { PageAction } from 'enums';
import {
  withSignUpCommonLayout,
  withSignUpCommonLayoutAndMarketingSection,
  withZipLogo,
  withZipLogoOnMobile,
} from 'components/sign-up';
import {
  AnnualSalesStepModule,
  BusinessInfoStepModule,
  EcommercePlatformStepModule,
  EligibilityCheckModule,
  PaymentProviderStepModule,
  PaymentUseCaseStepModule,
  POSPlatformStepModule,
  SignUpInfoStepModule,
  UserAccountCreationModule,
  VerifyEmailModule,
} from './steps';
import { useReCaptcha } from 'hooks';
import { Constants } from 'global';

export const SIGN_UP_STEP_COMPONENTS_MAP: Record<
  SignUpStepKey,
  ComponentType<StepComponentProps>
> = {
  signUpInfo: withZipLogoOnMobile(
    withSignUpCommonLayoutAndMarketingSection(SignUpInfoStepModule)
  ),
  businessInfo: withZipLogo(withSignUpCommonLayout(BusinessInfoStepModule)),
  paymentUseCase: withZipLogo(withSignUpCommonLayout(PaymentUseCaseStepModule)),
  ecommercePlatform: withZipLogo(
    withSignUpCommonLayout(EcommercePlatformStepModule)
  ),
  paymentProvider: withZipLogo(
    withSignUpCommonLayout(PaymentProviderStepModule)
  ),
  posPlatform: withZipLogo(withSignUpCommonLayout(POSPlatformStepModule)),
  annualSales: withZipLogo(withSignUpCommonLayout(AnnualSalesStepModule)),
  eligibilityCheck: withZipLogo(EligibilityCheckModule),
  userAccountCreation: withZipLogo(
    withSignUpCommonLayout(UserAccountCreationModule, {
      showNavButtons: false,
    })
  ),
  verifyEmail: withZipLogo(VerifyEmailModule),
};

const PAYMENT_TYPE_MAP: Record<string, string> = {
  'Online payments': 'Online',
  'Instore payments': 'Instore',
  'Both (online and instore)': 'Both',
};

const ANNUAL_SALES_MAP: Record<string, string> = {
  'Under $1M': 'Under1Million',
  '$1M - $10M': 'From1To10Million',
  '$10M - $50M': 'From10To50Million',
  'Over $50M': 'Over50Million',
};

function SignUpFlow(): JSX.Element {
  const [searchParams] = useSearchParams();

  const {
    createMerchantApplicationSignUpInfoAsync,
    patchMerchantApplicationSignUpInfoAsync,
    getApplicationFormOptionsAsync,
    mapServerError,
  } = useSignUpFunctions();
  const Snackbar = useSnackbar();
  const {
    merchantApplicationId,
    setMerchantApplicationId,
    stepState,
    nextSignUpStep,
    prevSignUpStep,
    commitStepData,
    setFormOptions,
    setFormOptionsLoading,
  } = useMerchantApplicationContext();

  const { execute, loadScript } = useReCaptcha();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const submitRef = useRef<(() => Promise<boolean>) | null>(null);
  const fieldErrorHandlerRef = useRef<((error: MappedError) => void) | null>(
    null
  );
  const canGoBack = stepState.activeIndex > 0;

  const ActiveStepComponent = useMemo<ComponentType<StepComponentProps>>(
    () => SIGN_UP_STEP_COMPONENTS_MAP[stepState.activeStepKey],
    [stepState.activeStepKey]
  );

  // Fetch form options once on mount
  useEffect(() => {
    loadScript();
    const fetchFormOptions = async (): Promise<void> => {
      try {
        const options = await getApplicationFormOptionsAsync();
        setFormOptions(options);
      } finally {
        setFormOptionsLoading(false);
      }
    };

    void fetchFormOptions();
  }, []);

  const buildSignUpDataModelFromContext = () => {
    const { activeStepKey, stepData } = stepState;
    return {
      activeStepKey,
      stepDetails: stepData.merchantApplicationDetails?.[activeStepKey],
      allStepDetails: stepData.merchantApplicationDetails,
      committedStepDetails: stepData.committedStepDetails,
      merchantApplicationId,
    };
  };

  const mapStepToApiFields = (
    key: SignUpStepKey,
    data: Record<string, any>
  ): Record<string, any> => {
    switch (key) {
      case SignUpStepKey.PaymentUseCase:
        return {
          ...(data.paymentType && {
            paymentType: PAYMENT_TYPE_MAP[data.paymentType] ?? data.paymentType,
          }),
        };
      case SignUpStepKey.EcommercePlatform:
        return {
          ...(data.platform && { eCommercePlatform: data.platform }),
          ...(data.otherValue && {
            eCommercePlatformOtherValue: data.otherValue,
          }),
        };
      case SignUpStepKey.PaymentProvider:
        return {
          ...(data.provider && { paymentProvider: data.provider }),
          ...(data.otherProvider && {
            paymentProviderOtherValue: data.otherProvider,
          }),
        };
      case SignUpStepKey.POSPlatform:
        return {
          ...(data.platform && { posPlatform: data.platform }),
          ...(data.otherPlatform && { posOtherValue: data.otherPlatform }),
        };
      case SignUpStepKey.AnnualSales:
        return {
          ...(data.salesRange && {
            totalAnnualSales:
              ANNUAL_SALES_MAP[data.salesRange] ?? data.salesRange,
          }),
        };
      default:
        // signUpInfo, businessInfo, eligibilityCheck, userAccountCreation — pass through as-is
        return data;
    }
  };

  const buildPatchPayload = (
    allStepDetails: ReturnType<
      typeof buildSignUpDataModelFromContext
    >['allStepDetails'],
    committedDetails: typeof stepState.stepData.committedStepDetails,
    currentStepKey: SignUpStepKey
  ) => {
    const committedCrossStepData = signUpStepsConfig
      .filter(({ key }) => key !== currentStepKey)
      .reduce(
        (combined, { key }) => ({
          ...combined,
          ...mapStepToApiFields(
            key,
            (committedDetails[key as keyof typeof committedDetails] ||
              {}) as Record<string, any>
          ),
        }),
        {}
      );

    const currentRaw = (allStepDetails[
      currentStepKey as keyof typeof allStepDetails
    ] || {}) as Record<string, any>;

    return {
      ...committedCrossStepData,
      ...mapStepToApiFields(currentStepKey, currentRaw),
    };
  };

  const createMerchantApplicationSignUpInfo = async (
    ctx: ReturnType<typeof buildSignUpDataModelFromContext>
  ): Promise<void> => {
    const referrer = searchParams.get('utm_campaign') || undefined;

    const payload = mapToMerchantApplicationSignUpDataModel(
      ctx.stepDetails,
      SignUpStepKey.SignUpInfo === stepState.activeStepKey
        ? referrer
        : undefined
    );

    const token = await execute(Constants.reCaptchaAction.applicationForm);

    const resp = await createMerchantApplicationSignUpInfoAsync(payload, token);

    setMerchantApplicationId(resp.applicationId);
  };

  const patchMerchantApplicationSignUpInfo = async (
    ctx: ReturnType<typeof buildSignUpDataModelFromContext>
  ): Promise<void> => {
    const combinedStepDetails = buildPatchPayload(
      ctx.allStepDetails,
      ctx.committedStepDetails,
      ctx.activeStepKey
    );

    const payload =
      mapToMerchantApplicationSignUpDataModel(combinedStepDetails);

    await patchMerchantApplicationSignUpInfoAsync(
      merchantApplicationId,
      payload
    );
  };

  const getPageUserActionForStep = (stepKey: SignUpStepKey): PageAction => {
    switch (stepKey) {
      case SignUpStepKey.SignUpInfo:
        return PageAction.merchantOnboardingUASignUp;
      case SignUpStepKey.BusinessInfo:
        return PageAction.merchantOnboardingUABusinessDetails;
      case SignUpStepKey.PaymentUseCase:
        return PageAction.merchantOnboardingUATradeChannel;
      case SignUpStepKey.EcommercePlatform:
        return PageAction.merchantOnboardingUAECommerce;
      case SignUpStepKey.PaymentProvider:
        return PageAction.merchantOnboardingUAPaymentProvider;
      case SignUpStepKey.POSPlatform:
        return PageAction.merchantOnboardingUAPOSPlatform;
      case SignUpStepKey.AnnualSales:
        return PageAction.merchantOnboardingUATTV;
    }
  };

  const handleError = useCallback(
    (error: MappedError): void => {
      if (isFormError(error)) {
        Snackbar.error(error.message);
      }
    },
    [Snackbar]
  );

  const handleContinueError = useCallback(
    (err: any): void => {
      const pageAction = !merchantApplicationId
        ? PageAction.merchantOnboardingUASignUp
        : getPageUserActionForStep(stepState.activeStepKey);

      logError(pageAction, err);

      const mapped = mapServerError(err);

      if (isFieldError(mapped)) {
        fieldErrorHandlerRef.current?.(mapped);
      } else {
        handleError(mapped);
      }
    },
    [
      handleError,
      mapServerError,
      merchantApplicationId,
      stepState.activeStepKey,
    ]
  );

  const handleContinue = async (): Promise<void> => {
    if (isSubmitting || !submitRef.current) {
      return;
    }

    setIsSubmitting(true);

    try {
      const isValid = await submitRef.current();

      if (!isValid) {
        return;
      }

      const context = buildSignUpDataModelFromContext();

      if (!merchantApplicationId) {
        await createMerchantApplicationSignUpInfo(context);
      } else {
        await patchMerchantApplicationSignUpInfo(context);
      }

      commitStepData(stepState.activeStepKey);

      const pageAction = !merchantApplicationId
        ? PageAction.merchantOnboardingUASignUp
        : getPageUserActionForStep(stepState.activeStepKey);
      logEvent(pageAction, { merchantApplicationId });

      nextSignUpStep();
    } catch (error) {
      if (isAbortError(error)) return;

      handleContinueError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = (): void => {
    if (!canGoBack || isSubmitting) {
      return;
    }

    prevSignUpStep();
  };

  const handleProvideSubmit = useCallback(
    (
      fn: () => Promise<boolean>,
      onFieldError?: (error: MappedError) => void
    ) => {
      submitRef.current = fn;
      fieldErrorHandlerRef.current = onFieldError ?? null;
    },
    []
  );

  return (
    <>
      <ActiveStepComponent
        provideSubmit={handleProvideSubmit}
        onError={handleError}
        canGoBack={canGoBack}
        canContinue={!isSubmitting}
        isSubmitting={isSubmitting}
        onBack={handleBack}
        onContinue={handleContinue}
      />
    </>
  );
}

export default SignUpFlow;
